/**
 * Widget Configuration Context
 *
 * Makes the widget config available to any component in the tree.
 * In standalone mode this context is not mounted, so consumers should
 * always provide a fallback via the DEFAULT_WIDGET_CONFIG export.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { WidgetConfig, DEFAULT_WIDGET_CONFIG } from './types';

const WidgetConfigContext = createContext<WidgetConfig>(DEFAULT_WIDGET_CONFIG);

interface WidgetConfigProviderProps {
  config: WidgetConfig;
  children: ReactNode;
}

export const WidgetConfigProvider: React.FC<WidgetConfigProviderProps> = ({
  config,
  children,
}) => {
  return (
    <WidgetConfigContext.Provider value={config}>
      {children}
    </WidgetConfigContext.Provider>
  );
};

/**
 * Hook to read widget config from any component.
 * Returns DEFAULT_WIDGET_CONFIG when used outside of a WidgetConfigProvider
 * (i.e. in standalone mode).
 */
export function useWidgetConfig(): WidgetConfig {
  return useContext(WidgetConfigContext);
}
